import axios from "../../axios-config";
import { persistStore } from "redux-persist";
import { store } from "../store";
import { del } from 'idb-keyval';
import { invalidateMetaCampaignCaches } from "../../queries/growthEngine/metaCampaign.query";
import { invalidateMetaConnectionStatus } from "../../queries/growthEngine/metaConnection.query";
import { queryClient as appQueryClient } from "../../queryClient";
import {
    getMetaErrorCodeFromAxiosError,
    isMetaTokenErrorCode,
} from "../../helper/metaTokenErrors";
import { queryClient } from "../../queryClient";
import { trackGrowthEngineActivity, GROWTH_ENGINE_ACTIVITY_TYPES } from "../../api/growthEngine/progress.api";
import { getCreditDetails } from "./billing";
import { notifyRewardCompletions } from "./backgroundProcesses";

const trackGrowthEngineActivitySafely = (activityType, metadata, dispatch) => {
    trackGrowthEngineActivity(activityType, metadata)
        .then((result) => {
            queryClient.invalidateQueries({ queryKey: ["rewards"] });
            // A reward may have just completed server-side, which may have
            // credited AI credits — refresh the sidebar balance immediately.
            if (dispatch && result?.milestonesReached?.length > 0) {
                dispatch(notifyRewardCompletions(result.milestonesReached));
                dispatch(getCreditDetails());
            }
        })
        .catch((err) => console.error("[GrowthEngine activity] tracking failed:", activityType, metadata, err));
}


const handleError = (toast, error, dispatch, platformType) => {
    // console.log(error,'error')
    const message = error.response?.data?.message || error.message;
    const status = error.response?.status;
    const code = getMetaErrorCodeFromAxiosError(error);

    // Token reconnect is handled by MetaConnectionBanner — keep toast light, do not break create/login flows
    if (isMetaTokenErrorCode(code)) {
        toast.error(message || "Meta connection issue. Please reconnect your account.", {
            position: "top-center",
            theme: "colored",
            hideProgressBar: true,
        });
        return;
    }

    if ([400, 401, 403, 404, 500].includes(status)) {
        toast.error(message, {
            position: "top-center",
            theme: "colored",
            hideProgressBar: true,
        });
        if (platformType === "YouTube" && status === 401 && dispatch) {
            dispatch(generateGoogleUrl(toast));
        }
    } else {
        toast.error(message, {
            position: "top-center",
            theme: "colored",
            hideProgressBar: true,
        });
    }
};

export const startGrowthEngine = () => {
    return {
        type: 'STARTGROWTHENGINE'
    };
}

export const growthEngineFail = () => {
    return {
        type: 'GROWTHENGINEFAIL'
    }
}
export const growthEngineSuccess = () => {
    return {
        type: 'GROWTHENGINESUCCESS'
    }
}

export const cancelFormDataState = () => {
    return {
        type: 'CANCEL_FORMDATA_STATE'
    }
}

/** Clear persisted Meta connection fields (pages, ad accounts, flags). */
export const clearMetaConnectionState = () => ({
    type: 'CLEAR_META_CONNECTION_STATE',
});

export const resetGrowthEngine = () => ({
    type: 'RESET_GROWTH_ENGINE',
});



export const setStep = (step) => {
    return {
        type: 'SET_STEP',
        payload: step,
    }
};

export const updateFormData = (data) => {
    return {
        type: 'UPDATE_FORM_DATA',
        payload: data,
    }
};

export const updateFormDataAdMedia = (data) => {
    return {
        type: 'UPDATE_FORM_DATA_AD_MEDIA',
        payload: data,
    };
};
export const updateTrackType = (type) => {
    return {
        type: 'HANDLE_TRACK_UPDATE',
        trackType: type,
    };
};

export const getDefaultTrackTypeForObjective = (objective) => {
    if (objective === "Playlist Growth") return "playlist";
    if (objective === "Spotify Growth") return "artist";
    return "track";
};

export const resetFeatureFmState = (objective) => ({
    type: 'RESET_FEATURE_FM_STATE',
    trackType: getDefaultTrackTypeForObjective(objective),
});

export const addArtistURL = (artistURL) => {
    return {
        type: 'ADD_ARTIST_URL',
        artistURL: artistURL,
    };
};

const META_REAUTH_NEXT_LOGIN_KEY = 'metaReauthNextLogin';

export const generateFacebookUrl = (toast, setLoading) => {
    return async (dispatch) => {
        dispatch(startGrowthEngine());
        if (setLoading) setLoading(true);
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const forceReauth = sessionStorage.getItem(META_REAUTH_NEXT_LOGIN_KEY) === '1';
            const response = await axios.get('/user/v1/generate-facebook-login-url', {
                headers: { Authorization: authCode },
                params: forceReauth ? { reauth: true } : undefined,
            });
            if (response.data.status === 200) {
                if (forceReauth) {
                    sessionStorage.removeItem(META_REAUTH_NEXT_LOGIN_KEY);
                }
                window.location.href = response.data.loginUrl;
                // Wait for Redux Persist to save before redirecting
                persistStore(store, null, () => {
                    console.log("Persisted Data Saved. Redirecting...", store);
                    window.location.href = response.data.loginUrl;
                });
            } else if (setLoading) {
                setLoading(false);
            }
        } catch (error) {
            dispatch(growthEngineFail());
            handleError(toast, error);
            if (setLoading) setLoading(false);
        }
    };
};


export const getAdAccount = (toast) => {
    return async (dispatch) => {
        // dispatch(startGrowthEngine());
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.post(`/user/v1/get-ad-account`, {}, {
                headers: { Authorization: authCode },
            });
            if (response.status === 200) {
                dispatch(updateFormData({ adAccountList: response.data.data }));
            } else {
                handleError(toast, response.data.message);
            }
        } catch (error) {
            console.log(error, 'error code====')
            dispatch(growthEngineFail());
            handleError(toast, error);
        }
    };
};

export const loadAdAccountMinBudget = (adAccountId, toast) => {
    return async () => {
        if (!adAccountId) return null;
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.get(`/campaign/v1/get-daily-min-budget`, {
                headers: { Authorization: authCode },
                params: { adAccountId },
            });
            if (response.status === 200 && response.data?.data) {
                const { currency, timezone, minDailyHighFreq } = response.data.data;
                return {
                    currency: currency || '',
                    timezone: timezone || '',
                    minDailyBudget: Number(minDailyHighFreq) || null,
                };
            }
        } catch (error) {
            console.log(error, 'loadAdAccountMinBudget error');
            if (toast) handleError(toast, error);
        }
        return null;
    };
};

export const fetchAdAccountBudgetLimits = (adAccountId, toast) => {
    return async (dispatch, getState) => {
        const limits = await dispatch(loadAdAccountMinBudget(adAccountId, toast));
        if (!limits) return;
        const currentBudget = Number(getState()?.growthEngine?.formData?.budget) || 0;
        const updates = {
            currency: limits.currency || '',
            timezone: limits.timezone || '',
            minDailyBudget: limits.minDailyBudget,
        };
        if (limits.minDailyBudget && currentBudget < limits.minDailyBudget) {
            updates.budget = Math.ceil(limits.minDailyBudget);
        }
        dispatch(updateFormData(updates));
    };
};

export const getPageList = (toast) => {
    return async (dispatch) => {
        // dispatch(startGrowthEngine());
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.post(`/user/v1/get-facebook-pages`, {}, {
                headers: { Authorization: authCode },
            });
            if (response.status === 200) {
                dispatch(updateFormData({ pageList: response.data.pages }));
            } else {
                handleError(toast, response.data.message);
            }
        } catch (error) {
            dispatch(growthEngineFail());
            handleError(toast, error);
        }
    };
};
export const getAccessToken = (code, toast, queryClient) => {
    return async (dispatch) => {
        dispatch(startGrowthEngine());
        dispatch(updateFormData({ metaAuthPending: true }));
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.get(`/user/v1/get-user-access-token?code=${code}`, {
                headers: { Authorization: authCode },
            });
            if (response.status === 200) {
                let facebookUserId = await response.data.facebookUserId
                // Part A: Meta token stays on backend — frontend only keeps connection flags
                dispatch(updateFormData({
                    metaLoggedIn: true,
                    metaAccessToken: '',
                    facebookUserId: facebookUserId,
                    metaAuthPending: false,
                }));
                if (queryClient) {
                    invalidateMetaConnectionStatus(queryClient);
                }
                await dispatch(getAdAccount(toast));
                await dispatch(getPageList(toast));
                // Clear one-time Meta OAuth code so refresh/logout doesn't retry exchange
                window.history.replaceState({}, "", window.location.pathname);
            } else {
                dispatch(updateFormData({ metaAuthPending: false }));
            }
        } catch (error) {
            dispatch(growthEngineFail());
            dispatch(updateFormData({ metaAuthPending: false }));
            handleError(toast, error);
            // Remove stale code on failure too; Meta auth codes are single-use
            window.history.replaceState({}, "", window.location.pathname);
        }
    };
};

export const getCurrentFacebookAccount = (toast) => {
    return async (dispatch) => {
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.get(`/user/v1/get-current-facebook-account`, {
                headers: { Authorization: authCode },
            });
            if (response.status === 200) {
                const connected = Boolean(response.data?.data?.connected);
                const facebookUserId = response.data?.data?.facebookUserId || '';
                dispatch(updateFormData({
                    metaLoggedIn: connected,
                    metaAccessToken: '',
                    facebookUserId,
                }));
                if (connected) {
                    await dispatch(getAdAccount(toast));
                    await dispatch(getPageList(toast));
                }
            }
        } catch (error) {
            dispatch(growthEngineFail());
            handleError(toast, error);
        }
    };
};

export const disconnectMetaAccount = (toast, queryClient) => {
    return async (dispatch) => {
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.post(
                '/user/v1/disconnect-meta-account',
                {},
                { headers: { Authorization: authCode } }
            );
            if (response.status === 200) {
                dispatch(updateFormData({
                    metaAccessToken: '',
                    metaLoggedIn: false,
                    facebookUserId: '',
                    adAccountList: [],
                    adAccountId: '',
                    pageList: [],
                    pageId: '',
                    pageAccessToken: '',
                    instagramId: '',
                    facebookMediaData: { photos: [], videos: [] },
                    instagramMediaData: [],
                    selectedPost: [],
                }));
                // Next Login to Meta should let user keep same account or switch
                sessionStorage.setItem(META_REAUTH_NEXT_LOGIN_KEY, '1');
                if (queryClient) {
                    invalidateMetaConnectionStatus(queryClient);
                    invalidateMetaCampaignCaches(queryClient);
                }
                toast.success(response.data?.message || 'Logged out from Meta. You can log in again.', {
                    position: 'top-center',
                    theme: 'colored',
                    hideProgressBar: true,
                });
            }
        } catch (error) {
            handleError(toast, error);
        }
    };
};

export const getTargetLocation = (data, toast) => {
    return async (dispatch) => {
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.post(`/user/v1/search-target-location`, data, {
                headers: { Authorization: authCode },
            });
            if (response.status === 200) {
                return response?.data?.data || []
            }
        } catch (error) {
            dispatch(growthEngineFail());
            handleError(toast, error);
        }
    };
};
export const getTargetlanguage = (data, toast) => {
    return async (dispatch) => {
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.post(`/user/v1/search-target-language`, data, {
                headers: { Authorization: authCode },
            });
            if (response.status === 200) {
                return response?.data?.data || []
            }
        } catch (error) {
            dispatch(growthEngineFail());
            handleError(toast, error);
        }
    };
};
export const getTargetIntrest = (data, toast) => {
    return async (dispatch) => {
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.post(`/user/v1/search-target-interest`, data, {
                headers: { Authorization: authCode },
            });
            if (response.status === 200) {
                return response?.data?.data || []
            }
        } catch (error) {
            dispatch(growthEngineFail());
            handleError(toast, error);
        }
    };
};
export const getSuggestedIntrest = (data, toast) => {
    return async (dispatch) => {
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.post(`/user/v1/get-interest-suggestion`, data, {
                headers: { Authorization: authCode },
            });
            if (response.status === 200) {
                dispatch(updateFormData({ suggestedIntrests: response?.data?.data || [] }));
            }
        } catch (error) {
            dispatch(growthEngineFail());
            handleError(toast, error);
        }
    };
};

export const getPotentialReach = (data, toast) => {
    return async (dispatch) => {
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.post(`/user/v1/get-reach-estimation`, data, {
                headers: { Authorization: authCode },
            });
            if (response.status === 200) {
                dispatch(updateFormData(
                    {
                        potentialReach:
                        {
                            min: response?.data?.data?.users_lower_bound,
                            max: response?.data?.data?.users_upper_bound
                        }
                    }));
            }
        } catch (error) {
            dispatch(growthEngineFail());
            handleError(toast, error);
        }
    };
};

export const getMetaAccountAssets = (data, toast, setMetaMediaLoading) => {
    return async (dispatch) => {
        try {
            if (setMetaMediaLoading) setMetaMediaLoading(true)
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.post(`/user/v1/get-user-meta-post`, data, {
                headers: { Authorization: authCode },
            });
            if (response.status === 200) {
                return await response.data
            }
        } catch (error) {
            dispatch(growthEngineFail());
            handleError(toast, error);
        } finally {
            if (setMetaMediaLoading) setMetaMediaLoading(false)
        }
    };
};

export const redirectToFeatureFM = (toast) => {
    return async (dispatch) => {
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.get(`/user/v1/redirect-to-feature-fm`, {
                headers: { Authorization: authCode },
            });
            if (response.status === 200) {
                window.open(response.data.redirectUrl, "_blank");
            }
        } catch (error) {
            dispatch(growthEngineFail());
            handleError(toast, error);
        }
    };
};

export const createCampaign = (data, toast, navigate, queryClient, callbacks = {}) => {
    return async (dispatch) => {
        dispatch(updateFormData({ isCampaignCreate: true }));
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.post(`/campaign/v1/create-meta-camapign`, data, {
                headers: { Authorization: authCode },
            });
            if (response.status === 200) {
                invalidateMetaCampaignCaches(queryClient || appQueryClient);
                callbacks.onSuccess?.();
                toast.success(response.data.message, {
                    position: "top-center",
                    theme: "colored",
                    hideProgressBar: true,
                });
                dispatch(cancelFormDataState());
                // Clear uploaded files from IndexedDB and localstorage
                await del("uploadedFiles");
                localStorage.removeItem("uploadedMedia");

                const metaCampaignId =
                    response.data?.data?.metaCampaignId ||
                    response.data?.metaCampaignId ||
                    null;

                if (metaCampaignId) {
                    trackGrowthEngineActivitySafely(GROWTH_ENGINE_ACTIVITY_TYPES.LAUNCH_CAMPAIGN, { campaignId: metaCampaignId }, dispatch);
                }

                setTimeout(() => {
                    dispatch(updateFormData({ isCampaignCreate: false }));
                    if (metaCampaignId) {
                        navigate(`/admin/growth-engine/campaign-editor/${metaCampaignId}`);
                    } else {
                        navigate("/admin/growth-engine");
                    }
                }, 1500);
            }
        } catch (error) {
            dispatch(growthEngineFail());
            handleError(toast, error);
            callbacks.onError?.();
            dispatch(updateFormData({ isCampaignCreate: false }));
        }
    };
};


export const getMetaCampaign = (list) => {
    return {
        type: 'GET_META_CAMPAIGN_LIST',
        list: list,
    }
};

export const getMetaCamapignList = (toast, setMetaCamapignLoading) => {
    return async (dispatch) => {
        setMetaCamapignLoading(true)
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.get(`/campaign/v1/get-meta-camapign-list`, {
                headers: { Authorization: authCode },
                params: { page: 1, limit: 10 },
            });
            if (response.status === 200) {
                dispatch(getMetaCampaign(response?.data?.data));
                setMetaCamapignLoading(false)
            }
            setMetaCamapignLoading(false)

        } catch (error) {
            dispatch(growthEngineFail());
            handleError(toast, error);
            setMetaCamapignLoading(false)
        }
    };
};
export const getMetaCampaignHistory = (data) => {
    return {
        type: 'GET_META_CAMPAIGN_HISTORY_LIST',
        data: data,
    }
};

export const getMetaCampignHistoryList = (toast, data, setCampaignLoading) => {
    return async (dispatch) => {
        setCampaignLoading(true)
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.get(`/campaign/v1/get-meta-camapign-history-list`, {
                headers: { Authorization: authCode },
                params: {
                    page: data.page, limit: data.limit,
                    interval: data?.interval || '', effectiveStatus: data?.effectiveStatus || ''
                },
            });
            if (response.status === 200) {
                dispatch(getMetaCampaignHistory(response?.data?.data));
                setCampaignLoading(false)
            }
            setCampaignLoading(false)

        } catch (error) {
            dispatch(growthEngineFail());
            handleError(toast, error);
            setCampaignLoading(false)
        }
    };
};

export const getMetaCampignDetails = (toast, campaignId, setCampaignLoading) => {
    return async (dispatch) => {
        setCampaignLoading(true)
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            // const authCode = "Bearer " + "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6IjY3Mzk0NjRmYjU2NjMzMDc5OTljMzhiOSIsImVtYWlsIjoibWdtdEBrYXJhbm11c2ljLmFydCIsInVzZXJUeXBlIjoidXNlciIsImFzc2lnbmVkQXJ0aXN0U29uZ3N0YXRlSWQiOiIwZnVzOW92NSIsImlhdCI6MTc1MzM0OTUwOH0.Ce3qyF7AUQJE6qKMXxM9dVBFKjyH26jPuMhZmDle5JQ";
            // const response = await axios.get(`/campaign/v1/get-meta-camapign-details/120231001516160498`, {
            //     headers: { Authorization: authCode }
            // });
            const response = await axios.get(`/campaign/v1/get-meta-camapign-details/${campaignId}`, {
                headers: { Authorization: authCode }
            });
            if (response.status === 200) {
                setCampaignLoading(false);
                return response?.data?.data || {};
            }

            setCampaignLoading(false);

        } catch (error) {
            dispatch(growthEngineFail());
            handleError(toast, error);
            setCampaignLoading(false)
        }
    };
};

export const updateMetaAdset = ({ data, closeEditGeographyModal, setShowLoading, toast, setRefreshFlag }) => {
    return async (dispatch) => {
        // return
        setShowLoading(true);
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.post(`/campaign/v1/update-meta-campaign-adsets`, data, {
                headers: { Authorization: authCode }
            });
            if (response.status === 200) {
                setShowLoading(false);
                toast.success(response.data.message, {
                    position: "top-center",
                    theme: "colored",
                    hideProgressBar: true,
                });
                invalidateMetaCampaignCaches(appQueryClient);
                if (data?.campaignId) {
                    trackGrowthEngineActivitySafely(GROWTH_ENGINE_ACTIVITY_TYPES.EDIT_CAMPAIGN, { campaignId: data.campaignId }, dispatch);
                }
                setTimeout(() => {
                    if (typeof setRefreshFlag === "function") {
                        setRefreshFlag(prev => !prev);
                    }
                    closeEditGeographyModal(false); // close modal
                }, 1000);
            }
        } catch (error) {
            dispatch(growthEngineFail());
            handleError(toast, error);
            setShowLoading(false)
            // closeEditGeographyModal(false)
        }
    };
};

export const updateMetaCampaign = ({ payload, closeEditBudgetModal, setShowLoading, toast, setRefreshFlag }) => {
    return async (dispatch) => {
        // return
        setShowLoading(true);
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.put(`/campaign/v1/update-meta-campaign`, payload, {
                headers: { Authorization: authCode }
            });
            if (response.status === 200) {
                setShowLoading(false);
                toast.success(response.data.message, {
                    position: "top-center",
                    theme: "colored",
                    hideProgressBar: true,
                });
                invalidateMetaCampaignCaches(appQueryClient);
                if (payload?.campaignId) {
                    trackGrowthEngineActivitySafely(GROWTH_ENGINE_ACTIVITY_TYPES.EDIT_CAMPAIGN, { campaignId: payload.campaignId }, dispatch);
                }
                setTimeout(() => {
                    if (typeof setRefreshFlag === "function") {
                        setRefreshFlag(prev => !prev);
                    }
                    closeEditBudgetModal(false); // close modal
                }, 1000);
            }
        } catch (error) {
            dispatch(growthEngineFail());
            handleError(toast, error);
            setShowLoading(false)
            // closeEditGeographyModal(false)
        }
    };
};

export const selectCampaignListPlatform = (platform) => {
    return {
        type: 'SELECT_CAMPAIGNLIST_PLATFORM',
        platform: platform
    }
};

export const getTracks = (list, trackType) => {
    return {
        type: 'GET_TRACK_LIST',
        list: list,
        trackType: trackType
    }
};

export const getTracklist = (type, toast, setIsLoading) => {
    return async (dispatch) => {
        setIsLoading(true)
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.get(`/user/v1/get-artist-track`, {
                headers: { Authorization: authCode },
                params: { type },
            });
            if (response.status === 200) {
                // console.log(response?.data,'response?.data',type)
                let list = []
                if (type === 'track') {
                    list = response?.data?.data
                } else if (type === 'album') {
                    list = response?.data?.data
                } else if (type === 'playlist') {
                    list = response?.data?.data
                    // playlists
                }
                // console.log(response?.data,'list')
                dispatch(getTracks(list, type));
                setIsLoading(false)
            } else {
                handleError(toast, response.data.message);
                setIsLoading(false)
            }
        } catch (error) {
            dispatch(growthEngineFail());
            handleError(toast, error);
        }
    };
};

export const getTracklistWithSearch = (data, toast, setIsLoading) => {
    return async (dispatch) => {
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            let type = data.artistTrackType;
            let query = data.query;
            let artistName = data.artistName;
            let artistId = data.artistId;
            const spotifyUrlRegex = /(?:open\.spotify\.com\/|spotify:)(track|album|playlist)[/:]([a-zA-Z0-9]+)/;
            const match = query.match(spotifyUrlRegex);
            if (match) { type = match[1] }
            const response = await axios.get(`/user/v1/search-artist-track`, {
                headers: { Authorization: authCode },
                params: { type, query, artistName, artistId },
            });
            if (response.status === 200) {
                let list = []
                if (type === 'track') {
                    list = response?.data?.data;
                } else if (type === 'album') {
                    list = response?.data?.data
                } else if (type === 'playlist') {
                    list = response?.data?.data
                }
                dispatch(getTracks(list, type));
                setIsLoading(false)
            } else {
                handleError(toast, response.data.message);
                setIsLoading(false)
            }
        } catch (error) {
            dispatch(growthEngineFail());
            setIsLoading(false)
            handleError(toast, error);
        }
    };
};

export const selectRelease = (details) => {
    return {
        type: 'SELECT_RELEASE',
        details: details
    }
};
export const albumPlaylistTracks = (details) => {
    return {
        type: 'ALBUM_PLAYLIST_TRACKS',
        details: details
    }
};

export const getAlbumPlaylistTracks = (data, toast) => {
    return async (dispatch) => {
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            let type = data.type;
            let id = data.id;
            const response = await axios.get(`/user/v1/get-playlist-album-tracks`, {
                headers: { Authorization: authCode },
                params: { id, type },
            });

            if (response.status === 200) {
                const details = response?.data?.data;
                dispatch(albumPlaylistTracks(details));
            } else {
                handleError(toast, response.data.message);
            }
        } catch (error) {
            dispatch(growthEngineFail());
            handleError(toast, error);
        }
    };
};

export const selectAlbumPlaylistTrack = (details) => {
    return {
        type: 'SELECT_ALBUM_PLAYLIST_TRACK',
        details: details
    }
};

export const createSongSmartLink = (data, setIsSubmitting) => {
    return async (dispatch) => {
        setIsSubmitting(true)
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.post(`/user/v1/create-song-smartlink`, data, {
                headers: { Authorization: authCode },
            });
            if (response.status === 200) {
                const details = response?.data;
                const smartlink = details.data.smartLink;
                const smartLinkId = details.data.smartLinkId;
                dispatch(updateFormData({ destinationUrl: smartlink, destinationUrlTrackId: smartLinkId }));
                setIsSubmitting(false)
                return { success: true };
            } else {
                setIsSubmitting(false)
                return { success: false };
            }
        } catch (error) {
            setIsSubmitting(false)
            return { success: false };
        }
    };
};

export const updateDuplicateCampaign = (data) => {
    return {
        type: 'DUPLICATE_CAMPAIGN',
        payload: data,
    }
};

export const cancelDuplicateCampaign = () => {
    return {
        type: 'CANCEL_DUPLICATE_CAMPAIGN'
    }
}

export const duplicateCampaign = ({ data, toast, navigate, setShowLoading }) => {
    return async (dispatch) => {
        setShowLoading(true)
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.post(`/campaign/v1/duplicate-meta-camapign`, data, {
                headers: { Authorization: authCode },
            });
            if (response.status === 200) {
                setShowLoading(false)
                toast.success(response.data.message, {
                    position: "top-center",
                    theme: "colored",
                    hideProgressBar: true,
                });
                invalidateMetaCampaignCaches(appQueryClient);
                dispatch(cancelDuplicateCampaign());
                // Redirect to /admin/growth-engine
                setTimeout(() => {
                    navigate("/admin/growth-engine");
                }, 1500);
            }
        } catch (error) {
            setShowLoading(false)
            dispatch(growthEngineFail());
            handleError(toast, error);
        }
    };
};

export const getMetaCampaignAnalytic = (details) => {
    return {
        type: 'GET_META_CAMPAIGN_ANALYTICS',
        details: details,
    }
};

export const metaCampaignAnalytics = (details) => {
    return {
        type: 'META_CAMPAIGN_ANALYTICS',
        details: details,
    }
};

export const getMetaCampaignAudienceBreakDown = (details) => {
    return {
        type: 'GET_META_CAMPAIGN_AUDIENCE_BREAKDOWN',
        details: details,
    }
};

export const getMetaCampaignAnalytics = (range, setCampaignAnalyticsLoading) => {
    return async (dispatch) => {
        setCampaignAnalyticsLoading(true)
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.get(`/campaign/v1/get-meta-campaign-analytics`, {
                headers: { Authorization: authCode },
                params: { range }
            });
            if (response.status === 200) {
                // Soft backend response keeps charts alive with zeros when Meta token is dead
                dispatch(getMetaCampaignAnalytic(response.data.data || {
                    totals: { spend: 0, impressions: 0, clicks: 0, cpc: 0 },
                    timeline: [],
                }))
            }
        } catch (error) {
            if (!isMetaTokenErrorCode(getMetaErrorCodeFromAxiosError(error))) {
                // Non-token failures leave previous analytics; token issues already soft-return 200
            }
            setCampaignAnalyticsLoading(false)
        } finally {
            setCampaignAnalyticsLoading(false)
        }
    };
};

export const fetchMetaCampaignAnalytics = (range, setCampaignAnalyticsLoading) => {
    return async (dispatch) => {
        setCampaignAnalyticsLoading(true)
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.get(`/campaign/v1/get-meta-campaign-analytics`, {
                headers: { Authorization: authCode },
                params: { range }
            });
            if (response.status === 200) {
                dispatch(metaCampaignAnalytics(response.data.data || {
                    totals: { spend: 0, impressions: 0, clicks: 0, cpc: 0, currency: null, spendUsd: 0 },
                    timeline: [],
                }))
            }
        } catch (error) {
            setCampaignAnalyticsLoading(false)
        } finally {
            setCampaignAnalyticsLoading(false)
        }
    };
};

export const getMetaCampaignAudience = (range, setMetaCamapignAudienceLoading) => {
    return async (dispatch) => {
        setMetaCamapignAudienceLoading(true)
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.get(`/campaign/v1/get-meta-campaign-audience-breakdown`, {
                headers: { Authorization: authCode },
                params: { range }
            });
            if (response.status === 200) {
                const payload = response.data?.data ?? response.data;
                dispatch(getMetaCampaignAudienceBreakDown(payload))
            }
        } catch (error) {
            setMetaCamapignAudienceLoading(false)
        } finally {
            setMetaCamapignAudienceLoading(false)
        }
    };
};

// google and youtube actions
export const generateGoogleUrl = (toast) => {
    return async (dispatch) => {
        dispatch(startGrowthEngine());
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.get('/user/v1/generate-google-login-url', {
                headers: { Authorization: authCode },
            });
            if (response.data.status === 200) {
                window.location.href = response.data.loginUrl;
                // Wait for Redux Persist to save before redirecting
                persistStore(store, null, () => {
                    console.log("Persisted Data Saved. Redirecting...", store);
                    window.location.href = response.data.loginUrl;
                });
            }
        } catch (error) {
            dispatch(growthEngineFail());
            handleError(toast, error);
        }
    };
};

export const getGoogleAccessToken = (code, toast) => {
    return async (dispatch) => {
        dispatch(startGrowthEngine());
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.get(`/user/v1/get-google-access-token?code=${code}`, {
                headers: { Authorization: authCode },
            });
            if (response.status === 200) {
                dispatch(updateFormData({ isGoogleLogin: true }));
                window.history.replaceState({}, "", window.location.pathname);
            }
        } catch (error) {
            dispatch(growthEngineFail());
            handleError(toast, error, dispatch);
            window.history.replaceState({}, "", window.location.pathname);
        }
    };
};

export const getGoogleCustomerList = (toast) => {
    return async (dispatch, getState) => {
        const { formData } = getState().growthEngine;
        const platformType = formData?.platformType;
        const currentSelectedId = formData?.selectedCustomerId;
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.get(`/user/v1/get-user-google-customer-list`, {
                headers: { Authorization: authCode },
            });
            if (response.status === 200) {
                const fetchedList = response.data.data;
                const defaultCustomerId =
                    currentSelectedId && fetchedList.some(item => item.customerId === currentSelectedId)
                        ? currentSelectedId
                        : fetchedList[0]?.customerId || '';

                dispatch(updateFormData({
                    customerIdList: fetchedList,
                    selectedCustomerId: defaultCustomerId,
                }));
            } else {
                handleError(toast, response.data.message);
            }
        } catch (error) {
            dispatch(growthEngineFail());
            handleError(toast, error, dispatch, platformType);
        }
    };
};

export const getYoutubeVideosList = (channelId, toast, setIsLoading, options = {}) => {
    const normalizedOptions = typeof options === "number"
        ? { maxResults: options }
        : options;
    const { type, pageToken, maxResults = 20 } = normalizedOptions;

    return async (dispatch) => {
        setIsLoading(true);
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.get(`/youtube/v1/get-youtube-channel-videos`, {
                headers: { Authorization: authCode },
                params: {
                    channelId,
                    maxResults,
                    ...(type ? { type } : {}),
                    ...(pageToken ? { pageToken } : {}),
                },
            });
            if (response.status === 200) {
                const videos = response?.data?.data?.videos || [];
                const shorts = response?.data?.data?.shorts || [];
                const pagination = response?.data?.data?.pagination || {
                    videos: { nextPageToken: null, hasMore: false },
                    shorts: { nextPageToken: null, hasMore: false },
                };
                if (!type) {
                    dispatch(updateFormData({
                        youtubeVideoList: videos,
                        channelVideoShortsList: { videos, shorts },
                    }));
                }
                return { videos, shorts, pagination };
            } else {
                handleError(toast, response.data.message);
            }
        } catch (error) {
            dispatch(growthEngineFail());
            handleError(toast, error, dispatch);
        } finally {
            setIsLoading(false);
        }
    };
};

export const searchYoutubeVideoAndShorts = (query, toast, setSearchLoading, options = {}) => {
    const { pageToken, maxResults = 20 } = options;

    return async (dispatch) => {
        setSearchLoading(true);
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.get(`/youtube/v1/search-youtube-video`, {
                headers: { Authorization: authCode },
                params: {
                    query,
                    maxResults,
                    ...(pageToken ? { pageToken } : {}),
                },
            });
            if (response.status === 200) {
                const videos = response?.data?.data?.videos || [];
                const shorts = response?.data?.data?.shorts || [];
                const pagination = response?.data?.data?.pagination || {
                    nextPageToken: null,
                    hasMore: false,
                };
                if (!pageToken) {
                    dispatch(updateFormData({
                        youtubeVideoList: [...videos, ...shorts],
                    }));
                }
                return { videos, shorts, pagination };
            } else {
                handleError(toast, response.data.message);
            }
        } catch (error) {
            dispatch(growthEngineFail());
            handleError(toast, error, dispatch);
        } finally {
            setSearchLoading(false);
        }
    };
};

export const getYoutubeTargetLocation = (query, toast) => {
    return async (dispatch) => {
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.get(`/youtube/v1/search-youtube-location`, {
                headers: { Authorization: authCode },
                params: {
                    query: query,
                }
            });
            if (response.status === 200) {
                return response?.data?.data || []
            }
        } catch (error) {
            dispatch(growthEngineFail());
            handleError(toast, error, dispatch);
        }
    };
};

export const getSuggestedChannel = (query, toast) => {
    return async (dispatch) => {
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.get(`/youtube/v1/search-youtube-channel`, {
                headers: { Authorization: authCode },
                params: {
                    query: query
                }
            });
            if (response.status === 200) {
                return response?.data?.data || []
            }
        } catch (error) {
            dispatch(growthEngineFail());
            handleError(toast, error, dispatch);
        }
    };
};

export const isGoogleConnected = () => {
    return async (dispatch) => {
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.get(`/user/v1/is-google-connected`, {
                headers: { Authorization: authCode },
            });
            if (response.status === 200) {
                dispatch(updateFormData({ isGoogleLogin: response?.data?.isGoogleconnected }));
            }
        } catch (error) {
            dispatch(growthEngineFail());
        }
    };
};

export const getYoutubeTargetLanguage = (query, toast) => {
    return async (dispatch) => {
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.get(`/youtube/v1/get-youtube-campaign-language`, {
                headers: { Authorization: authCode },
                params: {
                    query: query,
                }
            });
            if (response.status === 200) {
                return response?.data?.data || []
            }
        } catch (error) {
            dispatch(growthEngineFail());
            handleError(toast, error, dispatch);
        }
    };
};

export const searchYoutubeChannel = (query, toast, setSearchLoading) => {
    return async (dispatch) => {
        setSearchLoading(true);
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.get(`/youtube/v1/search-youtube-channel`, {
                headers: { Authorization: authCode },
                params: {
                    query: query
                }
            });
            if (response.status === 200) {
                setSearchLoading(false)
                return response?.data?.data || [];
            } else {
                setSearchLoading(false)
                handleError(toast, response.data.message);
            }
        } catch (error) {
            dispatch(growthEngineFail());
            handleError(toast, error, dispatch);
            setSearchLoading(false)
        }
    };
};

export const connectYoutubeChannel = (data, toast, setConnectLoading) => {
    return async (dispatch, getState) => {
        setConnectLoading(true);
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.post(`/youtube/v1/connect-youtube-channel`, data, {
                headers: { Authorization: authCode },
            });
            if (response.status === 200) {
                const updatedChannels = response?.data?.data || [];
                const { formData } = getState().growthEngine;
                const updatedFormData = {
                    connectedChannels: updatedChannels,
                    selectedChannel:
                        formData.selectedChannel === data.channelId ? data.channelId : "",
                };
                await dispatch(updateFormData(updatedFormData));

                toast.success(response.data.message, {
                    position: "top-center",
                    theme: "colored",
                    hideProgressBar: true,
                });
            } else {
                handleError(toast, response.data.message);
            }
        } catch (error) {
            dispatch(growthEngineFail());
            handleError(toast, error, dispatch);

        } finally {
            setConnectLoading(false)
        }
    };
};
export const getConnectYoutubeChannel = (toast, setConnectLoading) => {
    return async (dispatch) => {
        setConnectLoading(true);
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.get(`/youtube/v1/get-connected-youtube-channel`, {
                headers: { Authorization: authCode },
            });
            if (response.status === 200) {
                await dispatch(updateFormData({ connectedChannels: response?.data?.data || [] }));
            }
        } catch (error) {
            dispatch(growthEngineFail());
            handleError(toast, error, dispatch);
        } finally {
            setConnectLoading(false)
        }
    };
};

export const removeYoutubeChannel = (channelId, toast, setRemovingChannelId) => {
    return async (dispatch, getState) => {
        setRemovingChannelId(channelId);
        try {
            const authCode = "Bearer " + localStorage.getItem('authToken');
            const response = await axios.delete(`/youtube/v1/remove-channel/${channelId}`, {
                headers: { Authorization: authCode },
            });

            if (response.status === 200) {
                const updatedChannels = response?.data?.data || [];
                const { formData } = getState().growthEngine;
                const updatedFormData = {
                    connectedChannels: updatedChannels,
                    selectedChannel: formData.selectedChannel === channelId ? "" : formData.selectedChannel,
                };

                await dispatch(updateFormData(updatedFormData));
                toast.success(response.data.message, {
                    position: "top-center",
                    theme: "colored",
                    hideProgressBar: true,
                });
            } else {
                handleError(toast, response.data.message);
            }
        } catch (error) {
            dispatch(growthEngineFail());
            handleError(toast, error, dispatch);
        } finally {
            setRemovingChannelId(null);
        }
    };
};
